import React, { useCallback, useMemo, useState } from 'react';
import {
  Card,
  Box,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaxBreakdownPopover from './TaxBreakdownPopover';
import useCurrency from '../../../../components/CurrencyProvider';

export interface SummaryCardProps {
  total: number;
  concession: number;
  tax: number;
  net: number;
  /** Show VAT/Tax amount row. @default true */
  showTax?: boolean;
}

/**
 * Text constants to avoid hardcoding and make i18n easier later.
 */
const TEXT = {
  totalAmount: 'Total Amount',
  concessionAmount: 'Concession Amount',
  taxAmount: 'VAT Amount',
  netAmount: 'Net Amount',
  taxInfoTooltip: 'View detailed VAT breakdown',
  taxPopoverTitle: 'VAT Amount',
  netInfoTooltip: 'View net amount breakdown',
  netPopoverTitle: 'Net Amount Breakdown',
} as const;

/**
 * Style constants to avoid inline objects in JSX.
 * Layout and basic look remain the same; we only add subtle transitions.
 */
const STYLES = {
  card: {
    p: 2.5,
    background: '#fafbfc',
    borderRadius: 3,
    boxShadow: 'none',
    transition: 'box-shadow 150ms ease, transform 150ms ease',
    '&:hover, &:focus-within': {
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1.5,
  },
  taxRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  taxLabelContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  taxDivider: {
    height: 2,
    background: '#E2E3E5',
    mt: 1,
    borderRadius: 1,
  },
  netRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  netLabelContainer: {
    display: 'flex',
    alignItems: 'center',
  },
} as const;

const SummaryCard: React.FC<SummaryCardProps> = ({
  total,
  concession,
  tax,
  net,
  showTax = true,
}) => {
  const [taxAnchorEl, setTaxAnchorEl] = useState<HTMLElement | null>(null);
  const [taxOpen, setTaxOpen] = useState(false);
  const [netAnchorEl, setNetAnchorEl] = useState<HTMLElement | null>(null);
  const [netOpen, setNetOpen] = useState(false);
  const { formatCurrency } = useCurrency();

  // Memoize tax items to avoid re-creating on every render.
  const taxItems = useMemo(
    () => [
      { label: 'Sales Tax (3%)', value: formatCurrency(6.00) },
      { label: 'VAT (2%)', value: formatCurrency(4.00) },
    ],
    [formatCurrency]
  );

  // Memoize net breakdown items
  const netBreakdownItems = useMemo(
    () => [
      { label: 'Total Charges', value: formatCurrency(total) },
      { label: 'Less: Concession', value: `- ${formatCurrency(concession)}` },
      { label: 'Taxable Amount', value: formatCurrency(total - concession) },
      { label: 'Add: Tax', value: `+ ${formatCurrency(tax)}` },
      { label: 'Net Amount', value: formatCurrency(net), isBold: true },
    ],
    [total, concession, tax, net, formatCurrency]
  );

  const handleTaxToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget as HTMLElement;

      setTaxOpen((prevOpen) => !(prevOpen && taxAnchorEl === target));
      setTaxAnchorEl((prevAnchor) =>
        prevAnchor && prevAnchor === target ? null : target
      );
    },
    [taxAnchorEl]
  );

  const handleTaxClose = useCallback(() => {
    setTaxOpen(false);
    setTaxAnchorEl(null);
  }, []);

  const handleNetToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget as HTMLElement;

      setNetOpen((prevOpen) => !(prevOpen && netAnchorEl === target));
      setNetAnchorEl((prevAnchor) =>
        prevAnchor && prevAnchor === target ? null : target
      );
    },
    [netAnchorEl]
  );

  const handleNetClose = useCallback(() => {
    setNetOpen(false);
    setNetAnchorEl(null);
  }, []);

  // Memoize formatted values
  const formattedValues = useMemo(
    () => ({
      total: formatCurrency(total),
      concession: formatCurrency(concession),
      tax: formatCurrency(tax),
      net: formatCurrency(net),
    }),
    [total, concession, tax, net, formatCurrency]
  );

  return (
    <Card
      elevation={0}
      sx={STYLES.card}
      // Card is still just a container; keep semantics the same.
      aria-label="Summary of charges"
      tabIndex={-1}
    >
      {/* Total Amount */}
      <Box sx={STYLES.row}>
        <Typography variant="subtitle2">{TEXT.totalAmount}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {formattedValues.total}
        </Typography>
      </Box>

      {/* Concession Amount */}
      <Box sx={STYLES.row}>
        <Typography variant="subtitle2">{TEXT.concessionAmount}</Typography>
        <Typography variant="body2">{formattedValues.concession}</Typography>
      </Box>

      {/* Tax Amount & Breakdown */}
      {showTax && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={STYLES.taxRow}>
            <Box sx={STYLES.taxLabelContainer}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                {TEXT.taxAmount}
              </Typography>

              <Tooltip title={TEXT.taxInfoTooltip} arrow enterTouchDelay={0}>
                <IconButton
                  onClick={handleTaxToggle}
                  size="small"
                  sx={{ p: 0, ml: 0.5, cursor: 'pointer', color: '#5A8AEA' }}
                  aria-label={TEXT.taxInfoTooltip}
                  aria-haspopup="dialog"
                  aria-expanded={taxOpen ? 'true' : 'false'}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <TaxBreakdownPopover
                title={TEXT.taxPopoverTitle}
                items={taxItems}
                anchorEl={taxAnchorEl}
                open={taxOpen}
                onClose={handleTaxClose}
              />
            </Box>

            <Typography variant="body2">{formattedValues.tax}</Typography>
          </Box>

          <Box sx={STYLES.taxDivider} />
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Net Amount */}
      <Box sx={STYLES.netRow}>
        <Box sx={STYLES.netLabelContainer}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {TEXT.netAmount}
          </Typography>

          <Tooltip title={TEXT.netInfoTooltip} arrow enterTouchDelay={0}>
            <IconButton
              onClick={handleNetToggle}
              size="small"
              sx={{ p: 0, ml: 1, mt: 0.5, cursor: 'pointer', color: '#5A8AEA' }}
              aria-label={TEXT.netInfoTooltip}
              aria-haspopup="dialog"
              aria-expanded={netOpen ? 'true' : 'false'}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <TaxBreakdownPopover
            title={TEXT.netPopoverTitle}
            items={netBreakdownItems}
            anchorEl={netAnchorEl}
            open={netOpen}
            onClose={handleNetClose}
          />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {formattedValues.net}
        </Typography>
      </Box>
    </Card>
  );
};

export default SummaryCard;
